/**
 * React bridge for the browser Entra sign-in.
 *
 * {@link module:auth/entra-browser} supplies the pure pieces — configuration,
 * the MSAL app factory, and a per-request token acquirer. This module wraps them
 * in a React context so the shell can read sign-in status, start an interactive
 * login, sign out, and obtain the `getAccessToken()` the HTTP platform runtime
 * needs. The MSAL lifecycle (`initialize()` then `handleRedirectPromise()`) runs
 * once on mount; rendering is not gated here — the shell decides what to show for
 * each {@link EntraAuthStatus}.
 *
 * The provider depends on a narrow {@link EntraAppClient} interface rather than a
 * concrete `PublicClientApplication`, so the lifecycle and login wiring are
 * unit-testable with a fake; the live redirect handshake is only verifiable
 * against real Entra.
 *
 * @module auth/entra-auth-context
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import {
  type AccountInfo,
  type AuthenticationResult,
  type EndSessionRequest,
  type RedirectRequest,
} from "@azure/msal-browser";

import {
  createEntraTokenAcquirer,
  createScopedTokenAcquirer,
  type EntraBrowserConfig,
  GRAPH_PHOTO_SCOPE,
  type SilentTokenClient,
} from "./entra-browser";
import {
  mintExchangeCode,
  probeCookieSession,
  redeemExchangeCode,
  revokeSession,
} from "./session-client";

/**
 * The MSAL surface the provider drives. `PublicClientApplication` satisfies it;
 * tests supply a fake. It extends {@link SilentTokenClient} (the token acquirer's
 * dependency) with the lifecycle and interactive-flow methods the provider needs.
 */
export interface EntraAppClient extends SilentTokenClient {
  initialize(): Promise<void>;
  handleRedirectPromise(): Promise<AuthenticationResult | null>;
  setActiveAccount(account: AccountInfo | null): void;
  loginRedirect(request?: RedirectRequest): Promise<void>;
  logoutRedirect(request?: EndSessionRequest): Promise<void>;
}

/**
 * Sign-in lifecycle state.
 * - `initializing` — MSAL is completing `initialize()` / redirect handling.
 * - `unauthenticated` — ready, but no signed-in account.
 * - `authenticated` — an account is active; `getAccessToken` will resolve.
 * - `error` — initialization failed; `error` holds the cause.
 */
export type EntraAuthStatus = "initializing" | "unauthenticated" | "authenticated" | "error";

/**
 * Which credential the active session rests on.
 *
 * - `entra` — an MSAL account cached for this origin; requests carry a bearer
 *   token acquired from it.
 * - `cookie` — a host-only session cookie minted by the sign-in-origin bounce.
 *   MSAL's cache is per-origin and the interactive flow only ever runs on
 *   `loginOrigin`, so a tenant host has no MSAL account and never will; the
 *   cookie is the only credential it has.
 * - `null` — unauthenticated.
 */
export type EntraSessionKind = "entra" | "cookie" | null;

export interface EntraAuthValue {
  status: EntraAuthStatus;
  /** The signed-in account, or `null` when unauthenticated or cookie-backed. */
  account: AccountInfo | null;
  /** Which credential backs the active session. */
  sessionKind: EntraSessionKind;
  /**
   * The signed-in user's id. Read from MSAL's account on the sign-in origin and
   * from the API's own session on a cookie-backed host, so the shell has one
   * identifier either way.
   */
  userId: string | null;
  /** Initialization error, when `status` is `error`. */
  error: Error | null;
  /** Start an interactive redirect sign-in. Resolves as the browser navigates away. */
  login: () => Promise<void>;
  /**
   * Re-authenticate as a different account, keeping the caller where they are.
   * Revokes the current session, then forces Entra's account picker instead of
   * silently reusing the cached account. Resolves as the browser navigates away.
   */
  switchUser: () => Promise<void>;
  /** Sign out and clear the session cache. */
  logout: () => Promise<void>;
  /** Acquire a fresh API access token for the active account. */
  getAccessToken: () => Promise<string>;
  /**
   * Acquire a Graph token for reading profile photos.
   *
   * Absent where there is no MSAL account to acquire from, which is the
   * cookie-backed and impersonated sessions and the development bypass. A
   * caller treats its absence as "no photo", the same as an unconsented scope.
   */
  getGraphToken?: () => Promise<string>;
  /** Enter cookie-only mode after a server-issued session cookie is created. */
  beginCookieSession: () => void;
  /** Restore bearer or cookie mode after an impersonated session has ended. */
  restoreEntraSession: () => Promise<void>;
}

/**
 * Query marker asking the sign-in origin to show Entra's account picker.
 *
 * Named after the OIDC `prompt` parameter it ends up setting. A tenant host has
 * no MSAL cache of its own, so "switch user" there can only be a request to the
 * sign-in origin; without this marker that origin would find its cached account,
 * mint an exchange code, and bounce the caller straight back as the same user.
 */
const SELECT_ACCOUNT_PARAM = "prompt";
const SELECT_ACCOUNT_VALUE = "select_account";

export interface EntraAuthProviderProps {
  config: EntraBrowserConfig;
  /** The MSAL client. Injectable so the lifecycle can be tested without a live tenant. */
  app: EntraAppClient;
  children: React.ReactNode;
}

const EntraAuthContext = React.createContext<EntraAuthValue | null>(null);

/**
 * Provide Entra sign-in state and actions to the tree.
 *
 * On mount it initializes MSAL, completes any redirect sign-in in progress, and
 * adopts an account (the redirect result's, or the first cached account) as the
 * active one. Re-running is guarded so React strict-mode's double-invoke does not
 * initialize twice.
 */
export function EntraAuthProvider({ config, app, children }: EntraAuthProviderProps) {
  const [status, setStatus] = useState<EntraAuthStatus>("initializing");
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [sessionKind, setSessionKindState] = useState<EntraSessionKind>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // getAccessToken reads the kind through this ref rather than closing over the
  // state, so it keeps one identity for the life of the provider. Everything
  // downstream is memoised on it: the platform runtime, the client that runtime
  // builds, the session load keyed off that client, and the workspace load
  // keyed off that session. `sessionKind` settles as a session resolves, and
  // each transition used to hand every one of them a new identity, so starting
  // an impersonation ran the five-call workspace load three times over.
  //
  // The ref is written here rather than in an effect because the session
  // provider is a child, and a child's effects run before its parent's, so an
  // effect would still be holding the previous kind when the first request goes
  // out. Writing it beside the state update keeps it current from the moment
  // the transition is requested.
  const sessionKindRef = useRef<EntraSessionKind>(null);
  const setSessionKind = useCallback((next: EntraSessionKind): void => {
    sessionKindRef.current = next;
    setSessionKindState(next);
  }, []);
  const started = useRef(false);
  // A single ref shared across every effect invocation for this component
  // instance, not a value captured per-invocation. Under StrictMode's dev-only
  // mount/cleanup/mount, the first invocation's cleanup must not silence the
  // state update once the second invocation is the one left standing: an
  // `active` flag captured in the first invocation's closure did exactly that,
  // and initialize() never resolving to a status change is why an id
  // provisioned in SQL and reachable over HTTP still stalls on "Signing in".
  const active = useRef(true);

  useEffect(() => {
    active.current = true;
    if (!started.current) {
      started.current = true;
      (async () => {
        try {
          // Check for incoming socratiq_code from sign-in origin bounce
          if (typeof window !== "undefined" && window.location.search.includes("socratiq_code=")) {
            const searchParams = new URLSearchParams(window.location.search);
            const exchangeCode = searchParams.get("socratiq_code");
            if (exchangeCode) {
              try {
                await redeemExchangeCode(config.apiBaseUrl, exchangeCode);
                searchParams.delete("socratiq_code");
                const newSearch = searchParams.toString();
                const cleanUrl =
                  window.location.pathname +
                  (newSearch ? `?${newSearch}` : "") +
                  window.location.hash;
                window.history.replaceState({}, document.title, cleanUrl);
              } catch {
                // Ignore exchange error and fallback to MSAL check
              }
            }
          }

          await app.initialize();
          const redirect = await app.handleRedirectPromise();
          const resolved =
            redirect?.account ?? app.getActiveAccount() ?? app.getAllAccounts()[0] ?? null;
          if (resolved) {
            app.setActiveAccount(resolved);
          }

          // A switch-user handoff lands here carrying SELECT_ACCOUNT_PARAM. Show
          // the picker before anything else looks at the cached account, because
          // the bounce below would otherwise mint a code for whoever is already
          // signed in and the switch would silently do nothing.
          if (config.loginOrigin && typeof window !== "undefined") {
            const currentOrigin = window.location.origin.toLowerCase();
            const targetOrigin = config.loginOrigin.trim().toLowerCase().replace(/\/+$/, "");
            const params = new URLSearchParams(window.location.search);

            if (
              currentOrigin === targetOrigin &&
              params.get(SELECT_ACCOUNT_PARAM) === SELECT_ACCOUNT_VALUE
            ) {
              // Strip the marker before handing over. MSAL returns to whatever
              // URL was current when loginRedirect was called, so leaving it in
              // place would re-open the picker every time the user came back.
              params.delete(SELECT_ACCOUNT_PARAM);
              const query = params.toString();
              window.history.replaceState(
                {},
                document.title,
                window.location.pathname + (query ? `?${query}` : "") + window.location.hash,
              );
              await app.loginRedirect({
                scopes: [config.apiScope],
                prompt: SELECT_ACCOUNT_VALUE,
              });
              return;
            }
          }

          // If on sign-in origin and return URL is specified, bounce to return URL with exchange code
          if (resolved && config.loginOrigin && typeof window !== "undefined") {
            const currentOrigin = window.location.origin.toLowerCase();
            const targetOrigin = config.loginOrigin.trim().toLowerCase().replace(/\/+$/, "");
            const searchParams = new URLSearchParams(window.location.search);
            const returnUrl = searchParams.get("return");

            if (currentOrigin === targetOrigin && returnUrl) {
              const getAccessToken = createEntraTokenAcquirer(app, config);
              const token = await getAccessToken();
              const code = await mintExchangeCode(config.apiBaseUrl, returnUrl, token);
              if (!code) {
                throw new Error("Session exchange was refused");
              }

              const destUrl = new URL(returnUrl);
              destUrl.searchParams.set("socratiq_code", code);
              window.location.href = destUrl.toString();
              return;
            }
          }

          // Landed on the sign-in origin via the plain navigation `login()` does
          // from a tenant host, carrying `return` but no cached account yet.
          // Without this branch the mount effect falls through to
          // "unauthenticated" and re-renders the sign-in button, so the user
          // has to click it a second time before `loginRedirect` actually
          // fires and sends them to Microsoft.
          if (!resolved && config.loginOrigin && typeof window !== "undefined") {
            const currentOrigin = window.location.origin.toLowerCase();
            const targetOrigin = config.loginOrigin.trim().toLowerCase().replace(/\/+$/, "");
            const returnUrl = new URLSearchParams(window.location.search).get("return");

            if (currentOrigin === targetOrigin && returnUrl) {
              await app.loginRedirect({ scopes: [config.apiScope] });
              return;
            }
          }

          // No MSAL account is the normal, permanent state on every host except
          // the sign-in origin, because MSAL's cache is per-origin and the
          // interactive flow only ever runs there. The credential a tenant host
          // holds is the session cookie the bounce just redeemed, so ask the API
          // about it rather than concluding the caller is signed out — that
          // conclusion is what sent a signed-in user back to the sign-in button,
          // in a loop, with a perfectly good cookie in the jar.
          const cookieSession = resolved
            ? { authenticated: false, userId: null }
            : await probeCookieSession(config.apiBaseUrl);

          if (active.current) {
            setAccount(resolved);
            if (resolved) {
              setSessionKind("entra");
              setUserId(resolved.localAccountId ?? resolved.homeAccountId ?? null);
              setStatus("authenticated");
            } else if (cookieSession.authenticated) {
              setSessionKind("cookie");
              setUserId(cookieSession.userId);
              setStatus("authenticated");
            } else {
              setSessionKind(null);
              setUserId(null);
              setStatus("unauthenticated");
            }
          }
        } catch (cause) {
          if (active.current) {
            setError(cause instanceof Error ? cause : new Error("Entra initialization failed"));
            setStatus("error");
          }
        }
      })();
    }

    return () => {
      active.current = false;
    };
  }, [app, config, setSessionKind]);

  const login = useCallback(async (): Promise<void> => {
    if (config.loginOrigin && typeof window !== "undefined") {
      const currentOrigin = window.location.origin.toLowerCase();
      const targetOrigin = config.loginOrigin.trim().toLowerCase().replace(/\/+$/, "");
      if (currentOrigin !== targetOrigin) {
        window.location.href = `${targetOrigin}/?return=${encodeURIComponent(window.location.href)}`;
        return;
      }
    }
    await app.loginRedirect({ scopes: [config.apiScope] });
  }, [app, config]);

  const switchUser = useCallback(async (): Promise<void> => {
    // Revoke the current session first. Without this the previous user's cookie
    // outlives the switch, and a tenant host keeps answering as them until it
    // expires, which is worse than not offering the option at all.
    try {
      await revokeSession(config.apiBaseUrl);
    } catch {
      // Ignore network errors; the picker below still re-establishes identity.
    }

    if (typeof window === "undefined") {
      return;
    }

    const targetOrigin = config.loginOrigin?.trim().toLowerCase().replace(/\/+$/, "");
    if (targetOrigin && window.location.origin.toLowerCase() !== targetOrigin) {
      // A tenant host has no MSAL cache to re-prompt against, so the picker can
      // only be raised at the sign-in origin. Carry the return address so the
      // caller comes back to the page they were on.
      const returnTo = encodeURIComponent(window.location.href);
      window.location.href = `${targetOrigin}/?return=${returnTo}&${SELECT_ACCOUNT_PARAM}=${SELECT_ACCOUNT_VALUE}`;
      return;
    }

    await app.loginRedirect({ scopes: [config.apiScope], prompt: SELECT_ACCOUNT_VALUE });
  }, [app, config]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await revokeSession(config.apiBaseUrl);
    } catch (cause) {
      // The cookie is the whole session, so a reload would still be signed in.
      // A Microsoft account is signed out of below whatever the server said.
      if (sessionKind === "cookie") {
        setError(cause instanceof Error ? cause : new Error("Unable to sign out"));
        return;
      }
    }
    // A cookie-backed host has no MSAL account to sign out of, and
    // `logoutRedirect()` on an uninitialised-for-this-origin cache either
    // throws or navigates somewhere with nothing to clear. Revoking the cookie
    // above *is* the logout; reloading lands on the signed-out screen.
    if (sessionKind === "cookie") {
      if (typeof window !== "undefined") {
        window.location.reload();
      }
      return;
    }
    await app.logoutRedirect();
  }, [app, config.apiBaseUrl, sessionKind]);

  const acquireEntraToken = useMemo(() => createEntraTokenAcquirer(app, config), [app, config]);

  // Its own resource, so its own silent acquisition: a token is per-resource,
  // and asking for the API and Graph together returns one for neither.
  const acquireGraphToken = useMemo(
    () => createScopedTokenAcquirer(app, [GRAPH_PHOTO_SCOPE]),
    [app],
  );

  const getAccessToken = useCallback(async (): Promise<string> => {
    // A cookie-backed session has no MSAL account to acquire a token from, and
    // needs none: the cookie rides every request (the HTTP client sends
    // `credentials: "include"`) and the API accepts it in place of a bearer
    // token. Returning an empty token makes the client omit the Authorization
    // header rather than throw, which is what left every API call failing even
    // after the cookie was successfully minted.
    if (sessionKindRef.current === "cookie") {
      return "";
    }
    return acquireEntraToken();
  }, [acquireEntraToken]);

  const beginCookieSession = useCallback((): void => {
    setSessionKind("cookie");
  }, [setSessionKind]);

  const restoreEntraSession = useCallback(async (): Promise<void> => {
    const resolved = app.getActiveAccount() ?? app.getAllAccounts()[0];
    if (resolved) {
      app.setActiveAccount(resolved);
      setAccount(resolved);
      setSessionKind("entra");
      setUserId(resolved.localAccountId ?? resolved.homeAccountId ?? null);
      setStatus("authenticated");
    } else {
      const cookieSession = await probeCookieSession(config.apiBaseUrl);
      if (cookieSession.authenticated) {
        setAccount(null);
        setSessionKind("cookie");
        setUserId(cookieSession.userId);
        setStatus("authenticated");
      } else {
        setAccount(null);
        setSessionKind(null);
        setUserId(null);
        setStatus("unauthenticated");
      }
    }
  }, [app, config.apiBaseUrl, setSessionKind]);

  const value = useMemo<EntraAuthValue>(
    () => ({
      status,
      account,
      sessionKind,
      userId,
      error,
      login,
      switchUser,
      logout,
      getAccessToken,
      getGraphToken: sessionKind === "entra" ? acquireGraphToken : undefined,
      beginCookieSession,
      restoreEntraSession,
    }),
    [
      status,
      account,
      sessionKind,
      userId,
      error,
      login,
      switchUser,
      logout,
      getAccessToken,
      acquireGraphToken,
      beginCookieSession,
      restoreEntraSession,
    ],
  );

  return <EntraAuthContext.Provider value={value}>{children}</EntraAuthContext.Provider>;
}

export function useOptionalEntraAuth(): EntraAuthValue | null {
  return useContext(EntraAuthContext);
}

/**
 * Read the Entra sign-in state. Must be called within an {@link EntraAuthProvider}.
 */
export function useEntraAuth(): EntraAuthValue {
  const value = useOptionalEntraAuth();
  if (!value) {
    throw new Error("useEntraAuth must be used within an <EntraAuthProvider>");
  }
  return value;
}
